/* eslint-disable react/jsx-no-bind */
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuPortal,
  DropdownMenuSeparator,
} from "~/components/shared/DropdownMenu";
import Icon from "~/components/shared/Icon";
import Badge from "~/components/shared/Badge";
import { useFilterStore, useStateCounts } from "~/components/build/Show/lib/useFilterStore";
import {
  FilterByOption,
  FILTER_STATE_CONFIG,
  StateOrder,
} from "~/components/build/Show/lib/filterConstants";
import { FilterStateIcon } from "~/components/build/Show/lib/FilterStateIcon";
import classNames from "classnames";
import { useState } from "react";
import { useTrack } from "~/components/build/Show/lib/useTrack";
import Count from "~/components/build/Show/components/Count";
import { useBuildPreferencesStore } from "~/components/build/Show/lib/useBuildPreferencesStore";

/**
 * @deprecated Prefer the generic `FilterMenu` + `FilterChips` components from
 * `~/components/build/Show/components/Filter`. This dropdown will be removed
 * once the steps toolbar has been ported across.
 */
export default function FilterByDropdown() {
  const filterBy = useFilterStore((state) => state.filterBy);
  const setFilterBy = useFilterStore((state) => state.setFilterBy);
  const resetFilters = useFilterStore((state) => state.resetFilters);
  const track = useTrack();
  const counts = useStateCounts();
  const [showEmpty, setShowEmpty] = useState(false);
  const showSkippedSteps = useBuildPreferencesStore((state) => state.showSkippedSteps);

  const visibleFilters = showSkippedSteps
    ? StateOrder
    : StateOrder.filter((filter) => filter !== FilterByOption.Ignored);
  const filtersWithResults = visibleFilters.filter((filter) => counts[filter] > 0);
  const filtersWithoutResults = visibleFilters.filter((filter) => counts[filter] === 0);

  function toggleFilterBy(option: FilterByOption) {
    let newFilterBy: FilterByOption[] = [];

    if (filterBy.includes(option)) {
      // If the filter is already applied, remove it
      newFilterBy = filterBy.filter((existingOption) => existingOption !== option);
    } else {
      // If the filter is not applied, add it
      newFilterBy = [...filterBy, option];
    }

    setFilterBy(newFilterBy);

    track("Change State Filter", {
      enabled: newFilterBy,
    });
  }

  function disableAutomaticClose(event: Event) {
    event.preventDefault();
  }

  const itemClassName =
    "rounded-none px-3 py-2 text-sm hover:bg-gray-100 aria-checked:bg-purple-100";

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) {
          const hasSelectedEmpty = filtersWithoutResults.some((filter) =>
            filterBy.includes(filter),
          );
          if (!hasSelectedEmpty) {
            setShowEmpty(false);
          }
        }
      }}
    >
      <div className="flex">
        <DropdownMenuTrigger asChild={true}>
          <button
            data-testid="filter-by-dropdown"
            className={classNames(
              "text-charcoal-700 inline-flex max-w-full items-center space-x-1 rounded-md px-2 py-1 text-sm transition-colors hover:bg-purple-100 hover:text-purple-600 focus:z-10",
              {
                "bg-purple-100 text-purple-600": filterBy.length > 0,
                "rounded-r-none": filterBy.length > 0,
              },
            )}
          >
            <Icon icon="heroicons/outline/funnel" className="h-3 w-3 shrink-0" />

            <div className="flex max-w-full items-center gap-1 truncate">
              {filterBy.length === 0 && "Filter"}
              {filterBy.length === 1 && (
                <>
                  State is{" "}
                  <span className="font-medium">{FILTER_STATE_CONFIG[filterBy[0]].label}</span>
                </>
              )}
              {filterBy.length > 1 && (
                <>
                  States{" "}
                  <Badge outline={true} className="ml-0 border-current leading-normal">
                    {filterBy.length}
                  </Badge>
                </>
              )}
            </div>

            {filterBy.length === 0 && (
              <Icon icon="down-triangle" className="flex-none" style={{ width: 7, height: 7 }} />
            )}
          </button>
        </DropdownMenuTrigger>

        {filterBy.length > 0 && (
          <button
            aria-label="Reset filters"
            onClick={() => {
              resetFilters();
              setShowEmpty(false);
            }}
            className="no-title-tooltip text-charcoal-700 flex w-7 items-center justify-center self-stretch rounded-md rounded-l-none border-l-[1px] border-white bg-purple-100 hover:bg-purple-100/75 hover:text-purple-600"
          >
            <Icon icon="heroicons/16/solid/x-mark" className="h-4 w-4" />
          </button>
        )}
      </div>

      <DropdownMenuPortal>
        <DropdownMenuContent
          className="px-0 py-1"
          align="start"
          alignOffset={-1}
          data-testid="filter-by-dropdown-content"
        >
          {filtersWithResults.map((filter) => (
            <DropdownMenuCheckboxItem
              className={itemClassName}
              id={filter}
              key={filter}
              onCheckedChange={() => toggleFilterBy(filter)}
              checked={filterBy.includes(filter)}
              onSelect={disableAutomaticClose}
            >
              <div className="grid min-w-0 flex-auto grid-cols-[1fr_auto] items-center gap-2 font-medium">
                <span className="flex items-center gap-1.5">
                  <FilterStateIcon filter={filter} />
                  {FILTER_STATE_CONFIG[filter].label}
                </span>
                <Count>{counts[filter]}</Count>
              </div>
            </DropdownMenuCheckboxItem>
          ))}

          {filtersWithoutResults.length > 0 && (
            <>
              <DropdownMenuSeparator className="mx-0 my-1 bg-gray-300" />
              {showEmpty ? (
                filtersWithoutResults.map((filter) => (
                  <DropdownMenuCheckboxItem
                    id={filter}
                    key={filter}
                    className={classNames(itemClassName, "opacity-75")}
                    onCheckedChange={() => toggleFilterBy(filter)}
                    checked={filterBy.includes(filter)}
                    onSelect={disableAutomaticClose}
                  >
                    <div className="grid min-w-0 flex-auto grid-cols-[1fr_auto] items-center gap-2 font-medium">
                      <span className="flex items-center gap-1.5">
                        <FilterStateIcon filter={filter} />
                        {FILTER_STATE_CONFIG[filter].label}
                      </span>
                    </div>
                  </DropdownMenuCheckboxItem>
                ))
              ) : (
                <button
                  className={classNames(
                    "text-charcoal-400 hover:text-charcoal-600 w-full rounded-sm px-2 py-1.5 text-left text-xs hover:bg-gray-100",
                    itemClassName,
                  )}
                  onClick={() => setShowEmpty(true)}
                >
                  {filtersWithoutResults.length} others with no results
                </button>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
}
